import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api, track } from "../lib/api";
import { useSEO, usePageView } from "../lib/seo";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const tracked = useRef(false);

  useSEO({ title: "Your guide is on its way — The Nanny Sleep Co.", description: "Thank you for your purchase." });
  usePageView("/payment/success");

  useEffect(() => {
    if (!sessionId) return setStatus("error");
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await api.paymentStatus(sessionId);
        if (res.payment_status === "paid") {
          setStatus("paid");
          if (!tracked.current) {
            tracked.current = true;
            track("purchase_complete", { session_id: sessionId, product: "gentle-sleep-foundations-guide" });
          }
          return;
        }
      } catch {}
      if (++attempts < 10) setTimeout(poll, 2000);
      else setStatus("pending");
    };
    poll();
  }, [sessionId]);

  return (
    <section className="max-w-2xl mx-auto px-4 sm:px-6 py-24 sm:py-32 text-center" data-testid="payment-success-page">
      {status === "checking" && (
        <>
          <Loader2 className="w-12 h-12 text-terra mx-auto mb-6 animate-spin" />
          <h1 className="font-serif text-3xl sm:text-4xl text-ink">Confirming your order…</h1>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 className="w-14 h-14 text-sage mx-auto mb-6" strokeWidth={1.5} />
          <h1 className="font-serif font-light text-4xl sm:text-5xl text-ink mb-5">Your guide is on its way.</h1>
          <p className="text-lg text-ink-soft leading-relaxed max-w-md mx-auto mb-9">
            Check your inbox for the Gentle Sleep Foundations Guide download link. If it hasn't arrived in a few minutes, peek in spam — or email hello@thenannysleepco.com.au and I'll fix it personally.
          </p>
          <Link to="/sleep-help" data-testid="success-sleep-help-link" className="btn-secondary">Keep reading sleep help</Link>
        </>
      )}
      {status === "pending" && (
        <>
          <Loader2 className="w-12 h-12 text-gold mx-auto mb-6 animate-spin" />
          <h1 className="font-serif text-3xl sm:text-4xl text-ink mb-4">Almost there…</h1>
          <p className="text-ink-soft">Your payment is still confirming. Refresh this page in a moment — or email me if it persists.</p>
        </>
      )}
      {status === "error" && (
        <>
          <XCircle className="w-14 h-14 text-terra mx-auto mb-6" strokeWidth={1.5} />
          <h1 className="font-serif text-3xl sm:text-4xl text-ink mb-4">I couldn't find that order</h1>
          <p className="text-ink-soft mb-8">If you were charged, email hello@thenannysleepco.com.au and I'll sort it out right away.</p>
          <Link to="/support-options" className="btn-primary">Back to support options</Link>
        </>
      )}
    </section>
  );
}
